import { 
  collection, 
  addDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy,
  where,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Collection Firestore
const COLLECTION_NAME = 'trimestres';

// Créer un nouveau trimestre
export const createTrimestre = async (trimestreData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...trimestreData,
      debut: Timestamp.fromDate(new Date(trimestreData.debut)),
      fin: Timestamp.fromDate(new Date(trimestreData.fin)),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    console.log('Trimestre créé avec ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Erreur lors de la création du trimestre:', error);
    throw error;
  }
};

// Récupérer tous les trimestres
export const fetchTrimestres = async () => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME), 
      orderBy('debut', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const trimestres = [];
    
    querySnapshot.forEach((doc) => {
      trimestres.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return trimestres;
  } catch (error) {
    console.error('Erreur lors de la récupération des trimestres:', error);
    throw error;
  }
};

// Récupérer le trimestre actuel (basé sur une propriété isActive ou la date)
export const getCurrentTrimestre = async () => {
  try {
    const now = new Date();
    const q = query(
      collection(db, COLLECTION_NAME),
      where('debut', '<=', Timestamp.fromDate(now)),
      where('fin', '>=', Timestamp.fromDate(now)),
      orderBy('debut', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Erreur lors de la récupération du trimestre actuel:', error);
    throw error;
  }
};

// Mettre à jour un trimestre
export const updateTrimestre = async (id, updateData) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    
    const dataToUpdate = {
      ...updateData,
      updatedAt: Timestamp.now()
    };
    
    // Convertir les dates si présentes
    if (updateData.debut) {
      dataToUpdate.debut = Timestamp.fromDate(new Date(updateData.debut));
    }
    if (updateData.fin) {
      dataToUpdate.fin = Timestamp.fromDate(new Date(updateData.fin));
    }
    
    await updateDoc(docRef, dataToUpdate);
    console.log('Trimestre mis à jour:', id);
  } catch (error) {
    console.error('Erreur lors de la mise à jour du trimestre:', error);
    throw error;
  }
};

// Supprimer un trimestre
export const deleteTrimestre = async (id) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, id));
    console.log('Trimestre supprimé:', id);
  } catch (error) {
    console.error('Erreur lors de la suppression du trimestre:', error);
    throw error;
  }
};

// Marquer un trimestre comme actuel (désactive les autres)
export const setTrimestreAsActive = async (id) => {
  try {
    // D'abord, désactiver tous les trimestres
    const allTrimestres = await fetchTrimestres();
    const updatePromises = allTrimestres.map(trimestre => 
      updateTrimestre(trimestre.id, { isActive: false })
    );
    await Promise.all(updatePromises);
    
    // Puis activer le trimestre sélectionné
    await updateTrimestre(id, { isActive: true });
    
    console.log('Trimestre marqué comme actuel:', id);
  } catch (error) {
    console.error('Erreur lors de la définition du trimestre actuel:', error);
    throw error;
  }
};

// Générer automatiquement les trimestres pour une année
export const generateTrimestresForYear = async (year) => {
  try {
    const trimestres = [
      {
        titre: `1er Trimestre ${year}`,
        debut: new Date(year, 0, 1), // 1er janvier
        fin: new Date(year, 2, 31), // 31 mars
        isActive: false
      },
      {
        titre: `2ème Trimestre ${year}`,
        debut: new Date(year, 3, 1), // 1er avril  
        fin: new Date(year, 5, 30), // 30 juin
        isActive: false
      },
      {
        titre: `3ème Trimestre ${year}`,
        debut: new Date(year, 6, 1), // 1er juillet
        fin: new Date(year, 8, 30), // 30 septembre
        isActive: false
      },
      {
        titre: `4ème Trimestre ${year}`,
        debut: new Date(year, 9, 1), // 1er octobre
        fin: new Date(year, 11, 31), // 31 décembre
        isActive: false
      }
    ];
    
    const createPromises = trimestres.map(trimestre => createTrimestre(trimestre));
    const results = await Promise.all(createPromises);
    
    console.log(`Trimestres générés pour l'année ${year}:`, results);
    return results;
  } catch (error) {
    console.error('Erreur lors de la génération des trimestres:', error);
    throw error;
  }
};

// Vérifier s'il y a des chevauchements de dates
export const validateTrimestresDates = async (newTrimestre, excludeId = null) => {
  try {
    const allTrimestres = await fetchTrimestres();
    const startDate = new Date(newTrimestre.debut);
    const endDate = new Date(newTrimestre.fin);
    
    const conflicts = allTrimestres.filter(trimestre => {
      if (excludeId && trimestre.id === excludeId) return false;
      
      const existingStart = new Date(trimestre.debut.seconds * 1000);
      const existingEnd = new Date(trimestre.fin.seconds * 1000);
      
      // Vérifier les chevauchements
      return (startDate <= existingEnd && endDate >= existingStart);
    });
    
    return {
      isValid: conflicts.length === 0,
      conflicts: conflicts
    };
  } catch (error) {
    console.error('Erreur lors de la validation des dates:', error);
    throw error;
  }
}; 